import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Checkbox } from "semantic-ui-react";
import { useHistory, useLocation, useParams } from "react-router";
import Button from "react-bootstrap/Button";
import { CheckboxProps } from "semantic-ui-react/dist/commonjs/modules/Checkbox/Checkbox";
import Spinner from "react-bootstrap/Spinner";
import { FaCheck } from "react-icons/fa";
import { TimedAlert } from "../../../components/TimedAlert";
import { ENTRATA_USER_PROPERTY_URL } from "./EntrataUser";
import useAuth from "../../login/AuthContext";
import {
  EntrataFloorPlans,
  EntrataNetworkMappings,
  LoadAvailableFloorPlans,
  SaveEntrataMappings,
  EntrataRunImport,
  LoadEntrataUsers,
  EntrataUser,
  LinkedProperties,
} from "../../../services/IntegrationApi";
import { useSnackbar } from "../../../hooks/useSnackbar";

import "./EntrataMappingContainer.css";
import { NetworkSearch } from "../NetworkSearch";
import { EntrataDebuggerModal } from "./EntrataDebuggerModal";

import { useNetwork } from "../../../hooks/useNetwork";
import { MainLayout } from "../../../layout/MainLayout";
import {EntrataMigrationModal} from "./EntrataMigrationModal";
import { NetworkInfo, EntrataMappingContainerProps } from "./types";
import { EntrataMappingTable } from "./EntrataMappingTable";

export const ENTRATA_MAPPING_URL = `${ENTRATA_USER_PROPERTY_URL}/mappings`;

const BoldButton = styled(Button)`
  font-weight: bold;
  margin-left: 8px;

  &:disabled {
    background-color: #6c757d;
    opacity: 0.35;
    cursor: default;
  }
`;
const Indicator = styled(Spinner)`
  margin-top: 64px;
`;
const CheckIcon = styled(FaCheck)`
  margin-bottom: 1px;
`;

const HeaderWrapper = styled.div`
  height: 86px;
  margin: 24px 0px;
`;
const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  margin-bottom: 16px;
  flex-direction: row;
  justify-content: space-between;
`;
const Centered = styled.div`
  width: 100%;
  display: flex;
  margin: 32px auto;
  flex-direction: row;
  justify-content: center;
`;

// @ts-ignore
const getMappingKey = (row) => (row.l1Id ? `${row.l1Id}-${row.l2Id}-${row.l3Id}${row.l4Id ? `-${row.l4Id}` : ""}` : "");

export const EntrataMappingContainer: React.FunctionComponent = () => {
  const location = useLocation<EntrataMappingContainerProps>();
  const { userId, propertyId: propertyIdParam } = useParams<{ userId: string; propertyId: string }>();
  const { showErrorSnackbar } = useSnackbar();

  const [nameOptions, setNameOptions] = useState({});
  const [isImportRunning, setImportRunning] = useState(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialLoading, setInitialLoading] = useState<boolean>(false);
  const [debugModal, setDebugModal] = useState<{ show: boolean }>({
    show: false,
  });

  const [migrationModalShow, setMigrationModalShow] = useState<boolean>(false);

  const [alertText, setAlertVisible] = useState<string | boolean>(false);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [l1Id, setL1Id] = useState<number | undefined>(location.state?.l1Id);
  const [user, setUser] = useState<EntrataUser | null>(location.state?.user || null);
  const [property, setProperty] = useState<LinkedProperties | null>(location.state?.property || null);
  const [data, setData] = useState<{
    floorPlans: EntrataFloorPlans[];
    spaceConfigs: string[];
  }>({ floorPlans: [], spaceConfigs: [] });

  const [networkMappings, setNetworkMapping] = useState<EntrataNetworkMappings[]>([]);
  const [l2StatusById, setL2StatusById] = useState<Record<number, string | null>>({});
  const updatesToSend = networkMappings.filter(({ updated, valid }) => updated && valid);

  const propertyId = property?.propertyId;

  const history = useHistory();
  const { getAuthToken, isWriteAccess } = useAuth();
  const { getL2OptionsAsync } = useNetwork();

  // Load user and property data when not available from location state
  useEffect(() => {
    const loadUserAndPropertyData = async () => {
      if (!user && !property && userId && !initialLoading) {
        setInitialLoading(true);
        try {
          let foundUser: EntrataUser | undefined;
          let page = 0;
          const maxPages = 10;

          // Search through pages until we find the user
          while (!foundUser && page < maxPages) {
            const results = await LoadEntrataUsers(page, await getAuthToken());
            foundUser = results.content.find((u: EntrataUser) => u.id.toString() === userId);

            if (results.content.length === 0) {
              break;
            }
            page++;
          }

          if (foundUser) {
            setUser(foundUser);
            // If user has properties, we need to determine which property to use
            if (foundUser.properties && foundUser.properties.length > 0) {
              // If propertyId is provided in URL, find and use that specific property
              if (propertyIdParam) {
                const foundProperty = foundUser.properties.find(
                  (p) => p.propertyId.toString() === propertyIdParam
                );
                if (foundProperty) {
                  setProperty(foundProperty);
                  setL1Id(foundProperty.l1Id);
                } else {
                  showErrorSnackbar('Property not found for this user');
                  history.push('/super-admin/entrata');
                  return;
                }
              }
              // If there's only one property, use it automatically
              else if (foundUser.properties.length === 1) {
                setProperty(foundUser.properties[0]);
                setL1Id(foundUser.properties[0].l1Id);
              }
              // If multiple properties, user will need to select one from the UI
              // Don't redirect - let them stay on the mappings page
            } else {
              // No properties available
              showErrorSnackbar('No properties found for this user');
              history.push('/super-admin/entrata');
              return;
            }
          } else {
            showErrorSnackbar('User not found');
            history.push('/super-admin/entrata');
            return;
          }
        } catch (error) {
          showErrorSnackbar('Failed to load user data');
          history.push('/super-admin/entrata');
          return;
        } finally {
          setInitialLoading(false);
        }
      }
    };

    loadUserAndPropertyData();
  }, [userId, propertyIdParam, getAuthToken, history, showErrorSnackbar]);

  const filterMappings = useMemo(() => {
    return networkMappings
      .filter((mapping) => {
        if(showArchived || mapping.extra) {
          return mapping;
        }
        const status = mapping.l2Id != null ? l2StatusById[mapping.l2Id as number] : undefined;
        return status !== "ARCHIVED" && status !== "CLOSED";
      });

  }, [networkMappings, showArchived, l2StatusById]);

  const onAddExtraMappingClick = useCallback(() => {
    const rowId = new Date().getTime();

    setNetworkMapping((networkMappings) => [
      ...networkMappings,
      {
        rowId,
        extra: true,
        updated: false,
        allowAccess: false,
        entrataPropertyId: String(property?.propertyId || ''),
      },
    ]);
  }, [networkMappings, property]);

  const loadData = useCallback(async () => {
    if (!user || !property) return;

    const nonSelectableKeys: string[] = [];
    const nonSelectableIds: number[] = [];
    const notFilledMappings: NetworkInfo[] = [];

    try {
      const response = await LoadAvailableFloorPlans(
        user.id,
        property.propertyId,
        await getAuthToken()
      );

      // Check if response is valid
      if (!response || !response.entrataFloorPlans) {
        showErrorSnackbar('Failed to load floor plans data');
        setLoading(false);
        return;
      }

    // ~~~~~ NETWORK START ~~~~~
    if (l1Id) {
      const networks = await getL2OptionsAsync(l1Id);
      const names = {};
      const statusMap: Record<number, string | null> = {};

      for (const network of networks) {
        statusMap[network.l2Id] = network.matchingRoundStatus;
        for (const l3 of network.l3) {
          if (!!l3.l4 && l3.l4.length > 0) {
            for (const l4 of l3.l4) {
              // default mapping for current network l2-l4 set
              const mapping = {
                extra: true,
                rowId: 0,
                l1Id: network.l1Id,
                l2Id: network.l2Id,
                l3Id: l3.l3Id,
                l4Id: l4.l4Id,
                allowAccess: false,
                entrataPropertyId: String(property?.propertyId || ''),
              };
              const currentKey = getMappingKey(mapping);
              const isFilledMappingExist = response.entrataNetworkMappings?.find(
                (mapping) => getMappingKey(mapping) === currentKey
              );

              if (!isFilledMappingExist) {
                // if mapping for current network l2-l4 set doesnt exist, we add default one to the table
                notFilledMappings.push(mapping);
              }

              // @ts-ignore
              names[
                `${network.l1Id}-${network.l2Id}-${l3.l3Id}-${l4.l4Id}`
              ] = `${network.l1Name} - ${network.l2Name} ${network.l2Id} - ${l3.name} ${l3.l3Id} - ${l4.name} ${l4.l4Id}`;
            }
          } else {
            const mapping = {
              extra: true,
              rowId: 0,
              l1Id: network.l1Id,
              l2Id: network.l2Id,
              l3Id: l3.l3Id,
              allowAccess: false,
              entrataPropertyId: String(property?.propertyId || ''),
            };
            const currentKey = getMappingKey(mapping);
            const isFilledMappingExist = response?.entrataNetworkMappings?.find(
              (mapping) => getMappingKey(mapping) === currentKey
            );

            if (!isFilledMappingExist) {
              // if mapping for current network l2-l4 set doesnt exist, we add default one to the table
              notFilledMappings.push(mapping);
            }
            // @ts-ignore
            names[
              `${network.l1Id}-${network.l2Id}-${l3.l3Id}`
            ] = `${network.l1Name} - ${network.l2Name} ${network.l2Id} - ${l3.name} ${l3.l3Id}`;
          }
        }
      }

      setNameOptions(names);
      setL2StatusById(statusMap);
    }
    // ~~~~~ NETWORK END ~~~~~

    // ~~~~~ MAPPING START ~~~~~
    
    const nextNetworkMapping = [...(response?.entrataNetworkMappings || []), ...notFilledMappings]
      // @ts-ignore
      .sort((a, b) => a.id - b.id)
      .map((row, rowId) => {
        const rowKey = getMappingKey(row);
        const isSelectable = nonSelectableKeys.includes(rowKey);

        if (!isSelectable) {
          nonSelectableKeys.push(rowKey);
          nonSelectableIds.push(rowId);
        }

        return {
          ...row,
          rowId,
          extra: isSelectable,
          allowAccess: row.allowAccess || false,
        };
      });

    setNetworkMapping(nextNetworkMapping);
    setData({
      floorPlans: response.entrataFloorPlans,
      spaceConfigs: response.entrataSpaceConfigurations,
    });
    // ~~~~~ MAPPING END ~~~~~

    setLoading(false);
    } catch (error) {
      showErrorSnackbar('Failed to load floor plans data');
      setLoading(false);
    }
  }, [user, property, l1Id, getAuthToken, showErrorSnackbar]);

  useEffect(() => {
    if (user?.id && property?.propertyId && l1Id) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, property?.propertyId, l1Id]);

  const saveChanges = async () => {
    if (!user || !propertyId) return;

    setSaving(true);

    try {
      const response = await SaveEntrataMappings(
        user.id,
        propertyId,
        updatesToSend,
        await getAuthToken()
      );

      if (response && response.ok) {
        await loadData();

        setAlertVisible(`Successfully saved ${updatesToSend.length} element${updatesToSend.length === 1 ? "" : "s"}!`);
      }
    } catch (error) { }

    setSaving(false);
  };

  const onNetworkInfoUpdate = useCallback((updatedNetworkInfo: NetworkInfo) => {
    const newMappings = networkMappings.map((network) => {
      if (network.rowId === updatedNetworkInfo.rowId) {
        const {
          entrataFloorPlan,
          entrataStartDate,
          entrataEndDate,
          entrataUnitSpace,
          dateType,
          l1Id,
        } = updatedNetworkInfo;

        return {
          ...updatedNetworkInfo,
          updated: true,
          valid: !!(l1Id && entrataFloorPlan && entrataStartDate && entrataEndDate && dateType && entrataUnitSpace),
        };
      }

      return network;
    });
    setNetworkMapping(newMappings);
  }, [networkMappings, networkMappings.length]);


  const handleImportBtnClick = async () => {
    if(l1Id) {
      setImportRunning(true);
      const result = await EntrataRunImport(l1Id, await getAuthToken());
      return result.ok;
    } 
  };

  // Show loading spinner while loading initial data
  if (initialLoading) {
    return (
      <MainLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <Spinner animation="border" variant="secondary" />
        </div>
      </MainLayout>
    );
  }

  // Show empty state if no user data
  if (!user) {
    return (
      <MainLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <div>Loading user data...</div>
        </div>
      </MainLayout>
    );
  }

  // Show property selector if user has multiple properties and none is selected
  if (!property && user.properties && user.properties.length > 1) {
    return (
      <MainLayout>
        <div style={{ padding: '50px' }}>
          <h3>Select a Property</h3>
          <div style={{ marginTop: '20px' }}>
            {user.properties.map((prop) => (
              <div
                key={prop.propertyId}
                style={{
                  padding: '15px',
                  margin: '10px 0',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  backgroundColor: '#f8f9fa'
                }}
                onClick={() => {
                  history.push(`/super-admin/entrata/user/${userId}/property/${prop.propertyId}/mappings`, {
                    user,
                    property: prop,
                    l1Id: prop.l1Id,
                  });
                }}
              >
                <strong>{prop.propertyName}</strong> (Property ID: {prop.propertyId}, Network ID: {prop.l1Id})
              </div>
            ))}
          </div>
        </div>
      </MainLayout>
    );
  }

  // Show message if no property is available
  if (!property) {
    return (
      <MainLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
          <div>No property data available. Please select a property from the user page.</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {isWriteAccess() && (
        <>
          <EntrataDebuggerModal
            show={debugModal.show}
            entrataUserId={user?.id || 0}
            entrataPropertyId={propertyId}
            onClose={() => setDebugModal({ ...debugModal, show: false })}
          />
          <EntrataMigrationModal show={migrationModalShow} l1Id={l1Id} oldPropertyId={propertyId} closeModal={() => setMigrationModalShow(false)}/>
          <HeaderWrapper>
            <ButtonsWrapper>
              <div>
                <Button variant="secondary" onClick={history.goBack}>
                  Go Back
                </Button>
                <BoldButton onClick={() => setDebugModal({ ...debugModal, show: true })}>Debug</BoldButton>
                {l1Id && (<BoldButton onClick={handleImportBtnClick} disabled={isImportRunning}>Run Import</BoldButton>)}
                {l1Id && (<BoldButton variant="danger" onClick={() => setMigrationModalShow(true)}>Migrate</BoldButton>)}
              </div>
              <div>
                <Checkbox
                  toggle
                  checked={showArchived}
                  label="Show Closed/Archived"
                  onChange={
                    (event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) => setShowArchived(data.checked as boolean) 
                  }
                />
                <BoldButton variant="success" onClick={saveChanges} disabled={!updatesToSend.length}>
                  Save {saving ? <Spinner animation="border" variant="light" size="sm" /> : <CheckIcon />}
                </BoldButton>
              </div>
            </ButtonsWrapper>

            <TimedAlert variant="success" visible={!!alertText} setVisible={setAlertVisible}>
              {alertText}
            </TimedAlert>
           
          </HeaderWrapper>

          {l1Id ? null : <NetworkSearch setNetwork={setL1Id} />}

          <EntrataMappingTable
            nameOptions={nameOptions}
            l2StatusById={l2StatusById}
            networkMappings={filterMappings}
            entrataFloorPlans={data.floorPlans}
            setNetworkMapping={setNetworkMapping}
            entrataSpaceConfigs={data.spaceConfigs}
            onNetworkInfoChange={onNetworkInfoUpdate}
            userId={user?.id}
            propertyId={propertyId}
          />
          {loading && <Indicator animation="border" variant="secondary" />}

          {!loading && (
            <Centered>
              <Button variant="primary" onClick={onAddExtraMappingClick}>
                Add Extra Mapping
              </Button>
            </Centered>
          )}
        </>
      )}
    </MainLayout>
  );
};
